/**
 * The visibility notice, shown once per person per version.
 *
 * Gates the hub rather than sitting in a corner as a dismissible banner: the
 * whole point is that someone reads it before AAS starts seeing their work, and
 * a notice you can ignore is one nobody has read.
 *
 * AAS staff are not shown it — they are the visibility being disclosed, not the
 * people it is being disclosed to.
 */
import {
  CONSENT_ACCEPT_LABEL,
  CONSENT_BODY,
  CONSENT_TITLE,
  CONSENT_VERSION,
} from '@commpass/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { supabase } from '../lib/supabase';

function useConsentAccepted() {
  const { session } = useAuth();
  const userId = session?.user?.id;
  return useQuery<boolean>({
    queryKey: ['hub-consent', userId, CONSENT_VERSION],
    enabled: !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('hub_consents')
        .select('version')
        .eq('user_id', userId!)
        .eq('version', CONSENT_VERSION)
        .maybeSingle();
      if (error) throw error;
      return !!data;
    },
  });
}

function useAcceptConsent() {
  const qc = useQueryClient();
  const { session } = useAuth();
  return useMutation({
    mutationFn: async () => {
      const user = session?.user;
      if (!user) throw new Error('Not signed in');
      const { error } = await supabase.from('hub_consents').insert({
        user_id: user.id,
        version: CONSENT_VERSION,
        email: user.email ?? null,
      });
      // A second acceptance of the same version is a no-op, not a failure.
      if (error && !String(error.message).toLowerCase().includes('duplicate')) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['hub-consent'] }),
  });
}

export function HubConsent({ children }: { children: React.ReactNode }) {
  const { session, isStaff, loading } = useAuth();
  const accepted = useConsentAccepted();
  const accept = useAcceptConsent();

  // Anonymous visitors and AAS staff pass straight through. So does anyone whose
  // consent status we can't read yet — blocking the hub on a failed query would
  // turn a database hiccup into an outage for everyone.
  if (loading || !session || isStaff) return <>{children}</>;
  if (accepted.isLoading || accepted.isError || accepted.data) return <>{children}</>;

  return (
    <div className="consent-scrim" role="dialog" aria-modal="true" aria-labelledby="consent-title">
      <div className="consent-card">
        <h2 id="consent-title">{CONSENT_TITLE}</h2>
        {CONSENT_BODY.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
        <button
          className="btn primary consent-accept"
          disabled={accept.isPending}
          onClick={() => accept.mutate()}
        >
          {accept.isPending ? 'One moment…' : CONSENT_ACCEPT_LABEL}
        </button>
        {accept.isError && (
          <div className="consent-err">
            That didn&apos;t save. Please try again, or tell your Agents Alliance contact.
          </div>
        )}
        <div className="consent-meta">Version {CONSENT_VERSION}</div>
      </div>
    </div>
  );
}
